import { storeToRefs } from 'pinia';
import { useClientAnalytics } from '@ilteducation/client-analytics';
import useMediaStore from '../stores/media';
import useSettingsStore from '../stores/setting';
import useReaderStore from '../stores/reader';
import useRenditionStore from '../stores/rendition';
import usePositionTrackingStore from '../stores/position-tracking';
import usePlaylistStore from '../stores/playlist';
import useNavigationTriggerStore from '../stores/navigation-trigger';

/**
 * The events that describe a change in reading position, and so are the only ones a
 * navigation trigger can explain. Every other event (PLAY_PAUSE_CLICKED, BOOK_CLOSED, ...)
 * leaves the pending trigger untouched, so a play/pause in between a navigation and its
 * location event can't swallow it.
 */
const LOCATION_EVENT_NAMES = ['PAGE_CHANGED', 'POSITION_CHANGED', 'AUDIO_LOCATION_CHANGED'];

export function sendBookReaderEventAnalytics(eventName: string): void {
  const clientAnalytics = useClientAnalytics();
  const { sendAnalyticsEvent } = clientAnalytics;
  const { getMediaSettingsForAnalytics } = useMediaStore();
  const { getBookPlayerSettingsForAnalytics } = useSettingsStore();
  const { bookId, book, readingSession, viewMode, isContinuous } = storeToRefs(useReaderStore());
  const { rendition, isReflowable } = storeToRefs(useRenditionStore());
  const { totalSpineItems, currentPlaylistTime } = storeToRefs(usePositionTrackingStore());
  const { totalDuration } = storeToRefs(usePlaylistStore());
  const mediaStore = useMediaStore();
  const { currentTrack, totalContinuousBookAudioDuration, continuousBookAudioTrackTimings } = storeToRefs(mediaStore);
  const { trackAnalyticsKey } = mediaStore;
  const { takeNavigationTrigger } = useNavigationTriggerStore();

  if (!book.value || !bookId.value || !readingSession.value) {
    return;
  }

  const isAudioOnly = viewMode.value === 'audio-only';

  let displayedRangeStartValue: number;
  let displayedRangeEndValue: number;
  let totalNumberOfPages: number;
  let bookType: 'FixedBookLocation' | 'ReflowableBookLocation' | 'AudioOnlyLocation' | 'ContinuousBookLocation';

  if (isAudioOnly) {
    displayedRangeStartValue = currentPlaylistTime.value;
    displayedRangeEndValue = currentPlaylistTime.value;
    // Total number of pages is not relevant for audio books since we listen to audio and not read pages, therefore we send 0.
    totalNumberOfPages = 0;
    bookType = 'AudioOnlyLocation';
  } else if (isContinuous.value) {
    // Each chapter's audio file restarts at 0, so the raw track clipBegin/clipEnd can't be compared across the book. We report the cumulative position from the pre-built timings map (mediaStore.continuousBookAudioTrackTimings) so the range grows monotonically and equals totalContinuousBookAudioDuration at the end.
    const timing = continuousBookAudioTrackTimings.value.get(trackAnalyticsKey(currentTrack.value) ?? '');
    displayedRangeStartValue = timing?.start ?? 0;
    displayedRangeEndValue = timing?.end ?? 0;
    totalNumberOfPages = 0;
    bookType = 'ContinuousBookLocation';
  } else {
    if (!rendition.value || !rendition.value.location?.start || !rendition.value.location?.end) {
      return;
    }
    if (!isReflowable.value) {
      displayedRangeStartValue = rendition.value.location.start.index;
      displayedRangeEndValue = rendition.value.location.end.index;
      totalNumberOfPages = totalSpineItems.value;
      bookType = 'FixedBookLocation';
    } else {
      displayedRangeStartValue = rendition.value.location.start.percentage * 100; // The percentage object is a number between 0 and 1 (i.e. 0.53178). I multiply with 100 to store a number between 0 and 100 (i.e. 53.178)
      displayedRangeEndValue = rendition.value.location.end.percentage * 100; // The percentage object is a number between 0 and 1 (i.e. 0.53178). I multiply with 100 to store a number between 0 and 100 (i.e. 53.178)
      totalNumberOfPages = 100;
      bookType = 'ReflowableBookLocation';
    }
  }

  // Taken only once we know the event will actually be sent
  const navigationTrigger = LOCATION_EVENT_NAMES.includes(eventName) ? takeNavigationTrigger() : undefined;

  sendAnalyticsEvent(
    'BookPlayerEventType',
    {
      bookId: bookId?.value,
      bookType,
      numberOfPages: totalNumberOfPages,
      displayedRangeStart: displayedRangeStartValue,
      displayedRangeEnd: displayedRangeEndValue,
      totalAudioDuration: isContinuous.value ? totalContinuousBookAudioDuration.value : totalDuration.value,
      eventName: eventName,
      readingSessionId: readingSession?.value,
      bookPlayerMode: isAudioOnly ? 'AudioOnlyMode' : 'BookReaderMode',
      navigationTrigger: navigationTrigger,

      ...getBookPlayerSettingsForAnalytics(),
      ...getMediaSettingsForAnalytics(),
    },
    eventName === 'BOOK_CLOSED',
  );
}
